import {
	isObject,
	isPlainObject,
	isPlainObjectWithValues,
} from 'shared/utils/object';
import {
	ErrorPayload,
	ErrorWithCode,
	ErrorWithMessage,
	ExtractedErrorData,
	SerializedErrorForLog,
} from './types';
import {
	DOCUMENT_LIMIT_EXCEEDED_ERROR_PARTS,
	DOCUMENTATION_CHANGED_MEANWHILE_ERROR_PARTS,
	INSPECTIONS_IN_PROGRESS_ERROR_PARTS,
	SITE_DELETE_CONFLICT_INSPECTIONS_IN_PROGRESS_MESSAGE_ID,
	STAGE_NOT_IN_WORKFLOW_ERROR_PARTS,
	STAGE_NOT_IN_WORKFLOW_MESSAGE_ID,
	STAGE_AWAITING_APPROVALS_ERROR_TYPE,
	STAGE_AWAITING_APPROVALS_MESSAGE_ID,
	STAGE_REJECTED_ERROR_TYPE,
	LINKED_DOCUMENTATION_WITHOUT_CURRENT_VERSION_ERROR_TYPE,
	STAGE_REJECTED_MESSAGE_ID,
	INSPECTION_ANSWERS_NOT_FILLED_ERROR_PARTS,
	INSPECTION_ANSWERS_NOT_FILLED_MESSAGE_ID,
	TOO_MANY_FILES_MESSAGE_ID,
	EXTENDED_ERROR_TOAST_DELAY,
	EXTENDED_ERROR_TOAST_MESSAGE_IDS,
} from './constants';

export type {
	ErrorPayload,
	ErrorWithMessage,
	ExtractedErrorData,
	SerializedErrorForLog,
} from './types';

function getErrorPayload(error: unknown): ErrorPayload | null {
	if (isPlainObject(error)) {
		return error;
	}
	if (
		error instanceof Error &&
		'responseBody' in error &&
		isPlainObject((error as { responseBody: unknown }).responseBody)
	) {
		return (error as { responseBody: ErrorPayload }).responseBody;
	}
	return null;
}

function hasTruthyProperty(
	payload: ErrorPayload,
	key: string
): boolean {
	return key in payload && Boolean(payload[key]);
}

function getStringProperty(
	payload: ErrorPayload,
	key: string
): string | undefined {
	const value = payload[key];
	return typeof value === 'string' ? value : undefined;
}

/**
 * An error, or any object carrying a text `message`: a plain object (e.g. an
 * error that crossed a channel) or a host object such as DOMException, which
 * is no `Error` in some runtimes. `getErrorMessage` and `chooseErrorMessage`
 * read the message through it.
 */
export function isErrorWithMessage(
	error: unknown
): error is ErrorWithMessage {
	return (
		error instanceof Error ||
		(isObject<{ message?: unknown }>(error) &&
			!Array.isArray(error) &&
			typeof error.message === 'string')
	);
}

export function isErrorWithCode(
	error: unknown
): error is ErrorWithCode {
	return (
		(error instanceof Error &&
			'code' in error &&
			typeof error.code === 'string') ||
		(isPlainObjectWithValues(error, ['code']) &&
			typeof error.code === 'string')
	);
}

/**
 * HTTP queue / API handler payloads shaped as `{ error: ... }`.
 * Used across serviceWorker responseHandlers (decision, workflow, project, etc.).
 */
export function isErrorResponsePayload(
	response: unknown
): response is { error: unknown } {
	return (
		isPlainObject(response) &&
		'error' in response &&
		Boolean(response.error)
	);
}

export function getErrorMessage(
	error: unknown,
	depth = 0
): string | null {
	if (isErrorWithMessage(error)) {
		return error.message;
	}

	if (typeof error === 'string') {
		return error;
	}

	if (depth >= 3) {
		return null;
	}

	const payload = getErrorPayload(error);
	if (!payload) {
		return null;
	}

	const nestedErrorObject =
		'errorObject' in payload ? payload.errorObject : undefined;
	const nestedError = 'error' in payload ? payload.error : undefined;

	return (
		getErrorMessage(nestedErrorObject, depth + 1) ||
		getErrorMessage(nestedError, depth + 1)
	);
}

export function getErrorCode(
	error: unknown,
	depth = 0
): string | null {
	if (isErrorWithCode(error)) {
		return error.code;
	}

	if (depth >= 3) {
		return null;
	}

	const payload = getErrorPayload(error);
	if (!payload) {
		return null;
	}

	const nestedErrorObject =
		'errorObject' in payload ? payload.errorObject : undefined;
	const nestedError = 'error' in payload ? payload.error : undefined;

	return (
		getErrorCode(nestedErrorObject, depth + 1) ||
		getErrorCode(nestedError, depth + 1)
	);
}

export function createErrorWithCode(
	message: string,
	code: string
): Error & ErrorWithCode {
	const error = new Error(message) as Error & ErrorWithCode;
	error.code = code;
	return error;
}

export function hasErrorDetails(error: unknown): boolean {
	const payload = getErrorPayload(error);

	if (!payload) {
		return false;
	}

	const hasErrorObject = hasTruthyProperty(payload, 'errorObject');
	const hasNestedError = hasTruthyProperty(payload, 'error');
	const hasMessage = Boolean(getStringProperty(payload, 'message'));

	return hasErrorObject || hasNestedError || hasMessage;
}

export function getErrorPayloadId(error: unknown): string | undefined {
	const payload = getErrorPayload(error);

	if (!payload) {
		return undefined;
	}

	return getStringProperty(payload, 'id');
}

const isInspectionsInProgressError = (message: string): boolean =>
	INSPECTIONS_IN_PROGRESS_ERROR_PARTS.every((part) =>
		message.includes(part)
	);

const isDocumentLimitExceededError = (message: string): boolean =>
	DOCUMENT_LIMIT_EXCEEDED_ERROR_PARTS.every((part) =>
		message.includes(part)
	);

const isStageNotInWorkflowError = (message: string): boolean => {
	const lowerMessage = message.toLowerCase();
	return STAGE_NOT_IN_WORKFLOW_ERROR_PARTS.every((part) =>
		lowerMessage.includes(part.toLowerCase())
	);
};

const isInspectionAnswersNotFilledError = (message: string): boolean => {
	const lowerMessage = message.toLowerCase();
	return INSPECTION_ANSWERS_NOT_FILLED_ERROR_PARTS.every((part) =>
		lowerMessage.includes(part.toLowerCase())
	);
};

function getErrorType(error: unknown): string | null {
	const payload = getErrorPayload(error);
	if (!payload) return null;
	
	const errorType = getStringProperty(payload, 'errorType');
	if (errorType) return errorType;
	
	const nestedError = 'error' in payload ? payload.error : undefined;
	if (nestedError && isPlainObject(nestedError)) {
		return getStringProperty(nestedError as ErrorPayload, 'errorType') ?? null;
	}
	
	return null;
}

export function isAwaitingApprovalsError(error: unknown): boolean {
	return getErrorType(error) === STAGE_AWAITING_APPROVALS_ERROR_TYPE;
}

export function isStageRejectedError(error: unknown): boolean {
	return getErrorType(error) === STAGE_REJECTED_ERROR_TYPE;
}

export function isLinkedDocumentationWithoutCurrentVersionError(error: unknown): boolean {
	return getErrorType(error) === LINKED_DOCUMENTATION_WITHOUT_CURRENT_VERSION_ERROR_TYPE;
}

/** Someone else changed the documentation since it was read, as the backend's message says. */
export function isDocumentationChangedMeanwhileError(error: unknown): boolean {
	const message = getErrorMessage(getErrorPayload(error));
	return message !== null && DOCUMENTATION_CHANGED_MEANWHILE_ERROR_PARTS.every((part) => message.includes(part));
}

export function getErrorToastHideDelay(messageId: string): number | undefined {
	if (
		(EXTENDED_ERROR_TOAST_MESSAGE_IDS as readonly string[]).includes(
			messageId
		)
	) {
		return EXTENDED_ERROR_TOAST_DELAY;
	}

	return undefined;
}

export function chooseErrorMessage(error: unknown): string | null {
	if (isAwaitingApprovalsError(error)) {
		return STAGE_AWAITING_APPROVALS_MESSAGE_ID;
	}

	if (isStageRejectedError(error)) {
		return STAGE_REJECTED_MESSAGE_ID;
	}

	const message = getErrorMessage(error);
	if (!message) return null;

	if (isInspectionsInProgressError(message)) {
		return SITE_DELETE_CONFLICT_INSPECTIONS_IN_PROGRESS_MESSAGE_ID;
	}

	if (isDocumentLimitExceededError(message)) {
		return TOO_MANY_FILES_MESSAGE_ID;
	}

	if (isStageNotInWorkflowError(message)) {
		return STAGE_NOT_IN_WORKFLOW_MESSAGE_ID;
	}

	if (isInspectionAnswersNotFilledError(message)) {
		return INSPECTION_ANSWERS_NOT_FILLED_MESSAGE_ID;
	}

	return null;
}

export function extractErrorData(error: unknown): ExtractedErrorData {
	if (isPlainObject(error) && typeof error.messageId === 'string') {
		const nestedError = 'error' in error ? error.error : undefined;
		return {
			messageId: error.messageId,
			error: nestedError,
		};
	}

	return { error };
}

/**
 * Messages browsers give a `fetch` that got no response (Chrome, Firefox,
 * Safari). Chrome adds the host in parentheses, e.g. `Failed to fetch (app.hustro.com)`.
 */
const NETWORK_FAILURE_MESSAGES =
	/^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed)( \([^)]+\))?$/;

/**
 * Whether a request never reached the server (offline, DNS, connection
 * reset). Also for an error that crossed a channel as a plain object, which
 * keeps only its name and message.
 */
export function isNetworkFailureError(error: unknown): boolean {
	if (!isObject<{ name?: unknown; message?: unknown }>(error)) {
		return false;
	}
	const { name, message } = error;
	return name === 'TypeError' && typeof message === 'string' && NETWORK_FAILURE_MESSAGES.test(message);
}

export function isAbortError(error: unknown): boolean {
	if (error instanceof Error) {
		return error.name === 'AbortError';
	}

	if (
		isPlainObjectWithValues(error, ['name']) &&
		typeof error.name === 'string'
	) {
		return error.name === 'AbortError';
	}

	// Host objects such as DOMException (not instanceof Error in some runtimes)
	if (
		isObject<{ name?: unknown }>(error) &&
		!Array.isArray(error) &&
		typeof error.name === 'string'
	) {
		return error.name === 'AbortError';
	}

	return false;
}

export function serializeErrorForLog(
	error: unknown
): SerializedErrorForLog | Record<string, unknown> {
	if (error instanceof Error) {
		const serialized: Record<string, unknown> = {
			name: error.name,
			message: error.message,
		};

		if (error.stack) {
			serialized.stack = error.stack;
		}

		const code = getErrorCode(error);
		if (code) {
			serialized.code = code;
		}

		if ('cause' in error && error.cause !== undefined) {
			serialized.cause =
				error.cause instanceof Error
					? serializeErrorForLog(error.cause)
					: String(error.cause);
		}

		// Error's standard fields (name/message/stack) are non-enumerable, but
		// custom subclasses (e.g. HttpError) attach useful own enumerable context
		// such as `status` and `responseBody`. Preserve them so logs stay
		// diagnosable instead of collapsing to a bare name/message.
		for (const key of Object.keys(error)) {
			if (key === 'cause' || key in serialized) {
				continue;
			}
			const value = (error as unknown as Record<string, unknown>)[key];
			serialized[key] =
				value instanceof Error ? serializeErrorForLog(value) : value;
		}

		return serialized;
	}

	if (typeof error === 'string') {
		return { message: error };
	}

	if (Array.isArray(error)) {
		try {
			return JSON.parse(JSON.stringify(error)) as Record<string, unknown>;
		} catch {
			return { value: String(error) };
		}
	}

	if (isPlainObject(error)) {
		try {
			return JSON.parse(JSON.stringify(error)) as Record<string, unknown>;
		} catch {
			return { value: String(error) };
		}
	}

	return { value: String(error) };
}

export function normalizeLogDetails(
	details: unknown
): object | undefined {
	if (details === undefined || details === null) {
		return undefined;
	}

	if (details instanceof Error) {
		return serializeErrorForLog(details);
	}

	if (typeof details === 'object') {
		return Object.fromEntries(
			Object.entries(details as Record<string, unknown>).map(
				([key, value]) => [
					key,
					value instanceof Error ? serializeErrorForLog(value) : value,
				]
			)
		);
	}

	return { value: String(details) };
}
